// People (02-DESIGN/01-identity/01-tenants-and-roles.md § mapping rows, § the
// first admin; 02-the-auth-service.md § expiry, kick and revocation;
// 01-the-console.md § the words, § the moves of 2026-11-01): who belongs to
// the workspace and in which role, as the rows show them — a row on a
// person's stable subject, or a pending row on their handle, *waiting for
// their first sign-in* — and the *access rules*, the rows on a role, group or
// organisation claim. A member's row opens their page,
// `people/<provider>/<claim>/<value>` (MemberPage.tsx); the invite's state is
// the row's secondary line, and *Send again*, *Edit*, *Disconnect* and
// *Remove* are in the row menu. *Add a person*, *Add a rule* and *Edit* are
// forms in the tab's place; *Sign everyone out* is the Members tab's
// secondary action, with its consequence. Impire holds no user directory: a
// person is in when a row matches their verified token.

import { Plus } from "lucide-react";
import { useState } from "react";

import { PLATFORM_NAME, PLATFORM_SIGN_IN } from "../../platform/names.ts";
import type { TenantProfile } from "../../platform/profile.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Avatar,
  Button,
  Choices,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  since,
  TextInput,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { FormPage, formOf, useFormState } from "../../ui/form.tsx";
import {
  ProviderID,
  Subjects,
  type MemberAddRequest,
  type Row,
  type RowPutRequest,
  type RowReply,
  type RowsListReply,
  type SignOutAllReply,
} from "../../wire/auth.ts";
import { Role, SERVICE } from "../../wire/identity.ts";
import { ConfirmDialog, Table, type Column } from "../../ui/table.tsx";
import { Tabs, useTab, type TabSpec } from "../../ui/tabs.tsx";
import { useSurface } from "../surface.tsx";
import { editForm, editOf, formTab, type PeopleForm, type Tab } from "./form.ts";
import { memberPath, rowKey, usePeopleActs, useSigning } from "./acts.tsx";
import { addedLine, inviteLine, workspaceLink, type AddedLine } from "./invite.ts";
import { isPerson, personName, roleChange, roleChangeWarning, splitRows, byProvider } from "./rows.ts";
import { useLocation } from "react-router";

/** The page's tabs (01-the-console.md § conventions: the pages that have tabs). */
const TABS: readonly TabSpec<Tab>[] = [
  { id: "members", label: "Members" },
  { id: "rules", label: "Access rules" },
];

/** personSub is the one fact under a person's name: their invite's state while they wait, their handle, or since when. */
function personSub(r: Row): string {
  return r.pending === true
    ? (inviteLine(r)?.text ??
        `waiting for their first sign-in · added ${since(r.written) || when(r.written)}`)
    : r.display !== undefined && r.display !== "" && r.display !== r.value
      ? r.value
      : `since ${when(r.written)}`;
}

export function PeoplePage({ tenant }: { tenant: TenantProfile }) {
  const s = useSurface();
  const { platform, origin } = usePage();
  const location = useLocation();
  const [rows, reload] = useLoad("rows", () =>
    s.bus.call<RowsListReply>(SERVICE, Subjects.rowsList(), undefined),
  );
  const form = useFormState<PeopleForm>();
  const { inUse, ownName, signsInWith } = useSigning(s, tenant);
  const [showKept, setShowKept] = useState(false);
  // The person whose role just changed, offered *Sign them out now* until dismissed.
  const [changed, setChanged] = useState<Row | undefined>(undefined);
  const acts = usePeopleActs(s, reload, () => {
    setChanged(undefined);
  });
  const [added, setAdded] = useState<AddedLine | undefined>(undefined);
  const [marked, setMarked] = useState<string | undefined>(undefined);
  const [tab, setTabNow] = useTab(TABS, formTab(formOf(location.state)));
  // Another tab closes the form standing in this one.
  const setTab = (t: Tab) => {
    if (form.open !== undefined) {
      form.close();
    }
    setTabNow(t);
  };
  const [askingSignOut, setAskingSignOut] = useState(false);
  const [signedOut, setSignedOut] = useState<SignOutAllReply | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);
  const link = workspaceLink(origin, s.tenant);
  const platformName = PLATFORM_NAME;

  // *Edit* stands in the tab it was opened from, over the row its form names.
  const edit = (r: Row) => {
    form.show(editForm(tab, rowKey(r)));
  };

  const { current, kept } = byProvider(rows.state === "ready" ? rows.value.rows : [], inUse);
  const { people, rules } = splitRows(current);
  const keptSplit = splitRows(kept);
  const keptFor = (k: readonly Row[]) =>
    k[0] === undefined
      ? ""
      : k[0].provider === ProviderID.platform
        ? PLATFORM_SIGN_IN
        : k[0].provider === ProviderID.managed
          ? "the managed sign-in"
          : ownName;
  const keptLine = (k: readonly Row[], one: string, many: string) =>
    k.length === 0 ? null : (
      <div className="small faint" style={{ marginTop: 10 }}>
        {k.length} {k.length === 1 ? one : many} kept for {keptFor(k)}, not in use.{" "}
        <button
          type="button"
          className="text-link"
          onClick={() => {
            setShowKept((v) => !v);
          }}
        >
          {showKept ? "Hide" : "Show"}
        </button>
        {showKept && (
          <ul>
            {k.map((r) => (
              <li key={rowKey(r)}>
                {isPerson(r) ? personName(r) : `${r.claim} = ${r.value}`} — {r.role}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  const addPerson = (
    <Button
      kind="primary"
      onClick={() => {
        form.show("person");
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Add a person
    </Button>
  );
  const addRule = (
    <Button
      kind="primary"
      onClick={() => {
        form.show("rule");
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Add a rule
    </Button>
  );
  const signOutAll = () => {
    setErr(undefined);
    s.bus.call<SignOutAllReply>(SERVICE, Subjects.signOutAll(), undefined).then(
      (reply) => {
        setSignedOut(reply);
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const editKey = editOf(form.open)?.key;
  const editingNow =
    editKey === undefined || rows.state !== "ready"
      ? undefined
      : rows.value.rows.find((r) => rowKey(r) === editKey);
  const formShown = form.open === "person" || form.open === "rule" || editingNow !== undefined;
  const action = formShown ? undefined : tab === "members" ? (
    <>
      <Button
        kind="danger-quiet"
        onClick={() => {
          setAskingSignOut(true);
        }}
      >
        Sign everyone out
      </Button>
      {addPerson}
    </>
  ) : (
    addRule
  );
  const memberColumns: Column<Row>[] = [
    {
      key: "person",
      header: "Person",
      width: "minmax(0, 1.6fr)",
      value: (r) => personName(r),
      text: (r) => `${personName(r)} ${personSub(r)}`,
      cell: (r) => (
        <span className="with-avatar">
          <Avatar name={personName(r)} small />
          <span className="cell">
            <span className="cell-main">{personName(r)}</span>
            <span className="cell-sub">{personSub(r)}</span>
          </span>
        </span>
      ),
    },
    {
      key: "role",
      header: "Role",
      width: "150px",
      facet: "Role",
      value: (r) => r.role,
      text: (r) => (r.pending === true ? `${r.role} waiting` : r.role),
      cell: (r) => (
        <span>
          <Pill strong={r.role === Role.admin}>{r.role}</Pill>
          {r.pending === true && <Pill>waiting</Pill>}
        </span>
      ),
    },
    {
      key: "with",
      header: "Signs in with",
      narrow: false,
      value: signsInWith,
      cell: (r) => <span className="muted">{signsInWith(r)}</span>,
    },
  ];
  const ruleColumns: Column<Row>[] = [
    {
      key: "rule",
      header: "Rule",
      width: "minmax(0, 2fr)",
      value: (r) => `${signsInWith(r)} ${r.claim} ${r.value}`,
      cell: (r) => (
        <span className="small">
          {signsInWith(r)} <span className="mono">{r.claim}</span> <span className="mono">{r.value}</span>
        </span>
      ),
    },
    {
      key: "gives",
      header: "Gives",
      width: "120px",
      facet: "Role",
      value: (r) => r.role,
      cell: (r) => <span className="small faint">→ {r.role}</span>,
    },
  ];

  /** The form standing in the tab's place, where one is open. */
  const shownForm =
    form.open === "person" ? (
      <AddPerson
        lane={inUse === ProviderID.platform ? platform.platform_provider.lane : "oidc"}
        onClose={form.close}
        onAdded={(row, handle) => {
          form.close();
          setAdded(addedLine(row, handle, link));
          setMarked(rowKey(row));
          reload();
        }}
      />
    ) : form.open === "rule" ? (
      <AddRule
        platformName={platformName}
        ownName={tenant.provider === undefined ? undefined : ownName}
        onClose={form.close}
        onAdded={(row) => {
          form.close();
          setMarked(rowKey(row));
          reload();
        }}
      />
    ) : editingNow !== undefined ? (
      <EditRole
        row={editingNow}
        rows={rows.state === "ready" ? rows.value.rows : []}
        who={isPerson(editingNow) ? personName(editingNow) : undefined}
        onClose={form.close}
        onChanged={(r) => {
          form.close();
          acts.setNote(undefined);
          // A person's change takes effect at their next connect; a pending
          // row has no connection, and a rule names no one to sign out.
          if (r.pending !== true && isPerson(r) && s.offered("people:kick")) {
            setChanged(r);
          } else {
            setChanged(undefined);
            acts.setNote(
              r.pending === true
                ? `${personName(r)} will be ${r.role === Role.admin ? "an admin" : "a member"} from their first sign-in.`
                : `The rule now gives ${r.role}, from each person's next sign-in.`,
            );
          }
          setMarked(rowKey(r));
          reload();
        }}
      />
    ) : undefined;

  return (
    <>
      <PageHead title="People" line={`Who belongs to ${s.name}, and in which role.`} action={action} />
      {err !== undefined && <NotAllowed err={err} />}
      {acts.err !== undefined && <NotAllowed err={acts.err} />}
      {acts.note !== undefined && shownForm === undefined && (
        <Notice kind="ok">
          {acts.note}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              acts.setNote(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}

      {changed !== undefined && shownForm === undefined && (
        <Notice kind="ok">
          {personName(changed)} is now {changed.role === Role.admin ? "an admin" : "a member"}, from their
          next sign-in.{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              acts.disconnect(changed);
            }}
          >
            Sign them out now
          </button>{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setChanged(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}

      {added !== undefined && shownForm === undefined && (
        <Notice kind={added.ok ? "ok" : "info"}>
          {added.text}
          {added.link !== undefined && (
            <>
              {" "}
              Their link: <span className="mono">{added.link}</span>{" "}
              <button
                type="button"
                className="text-link"
                onClick={() => {
                  void navigator.clipboard.writeText(added.link ?? "");
                }}
              >
                Copy
              </button>
            </>
          )}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setAdded(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}

      {signedOut !== undefined && (
        <Notice kind="ok">Everyone was signed out at {when(signedOut.at)}. You will be too.</Notice>
      )}
      {rows.state === "pending" && <Pending what="Reading who belongs" />}
      {rows.state === "refused" && <NotAllowed err={rows.err} what="People" />}
      {rows.state === "ready" && (
        <Tabs label="People" tabs={TABS} value={tab} onChange={setTab}>
          {shownForm}
          {shownForm === undefined &&
            tab === "members" &&
            (people.length === 0 ? (
              <Empty action={addPerson}>Nobody can sign in to {s.name} yet.</Empty>
            ) : (
              <Table
                label="Members"
                rows={people}
                rowKey={rowKey}
                sort={{ key: "person", dir: "asc" }}
                columns={memberColumns}
                mark={marked}
                to={(r) => memberPath(s.tenant, r)}
                menu={(r) => acts.memberMenu(r, edit)}
              />
            ))}
          {shownForm === undefined && tab === "members" && keptLine(keptSplit.people, "person", "people")}
          {shownForm === undefined && tab === "rules" && (
            <>
              <p className="small muted" style={{ marginBottom: 14 }}>
                Who gets which role when they sign in. A person with no matching rule is not allowed in.
              </p>
              {rules.length === 0 ? (
                <Empty action={addRule}>No rules. People are added one by one.</Empty>
              ) : (
                <Table
                  label="Access rules"
                  rows={rules}
                  rowKey={rowKey}
                  sort={{ key: "rule", dir: "asc" }}
                  columns={ruleColumns}
                  mark={marked}
                  menu={(r) => [
                    ...(r.required !== true
                      ? [
                          {
                            label: "Edit",
                            onSelect: () => {
                              edit(r);
                            },
                          },
                        ]
                      : []),
                    {
                      label: "Remove",
                      danger: true as const,
                      confirm: {
                        title: "Remove this rule?",
                        consequence: "A person this rule let in is not allowed at their next sign-in.",
                      },
                      onSelect: () => {
                        acts.remove(r);
                      },
                    },
                  ]}
                />
              )}
              {keptLine(keptSplit.rules, "rule", "rules")}
            </>
          )}
        </Tabs>
      )}
      {askingSignOut && (
        <ConfirmDialog
          ask={{
            title: "Sign everyone out?",
            consequence: `Every connection to ${s.name} closes, this one included. People sign in again with the same accounts.`,
          }}
          verb="Sign everyone out"
          onCancel={() => {
            setAskingSignOut(false);
          }}
          onConfirm={() => {
            setAskingSignOut(false);
            signOutAll();
          }}
        />
      )}
    </>
  );
}

const ROLES = [
  {
    value: Role.member,
    label: "Member",
    hint: "Uses the workspace: connects apps, links toolkits, uses its solutions.",
  },
  { value: Role.admin, label: "Admin", hint: "Also sets it up: people, sign-in, secrets, settings." },
];

function AddPerson({
  lane,
  onClose,
  onAdded,
}: {
  lane: string;
  onClose: () => void;
  onAdded: (row: Row, handle: string) => void;
}) {
  const s = useSurface();
  const [handle, setHandle] = useState("");
  const [role, setRole] = useState<string>(Role.member);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const submit = () => {
    if (busy || handle.trim() === "") {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const body: MemberAddRequest = { handle: handle.trim(), role: role as Role };
    s.bus.call<RowReply>(SERVICE, Subjects.membersAdd(), body).then(
      (reply) => {
        setBusy(false);
        onAdded(reply.row, handle.trim());
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      title="Add a person"
      line={`They appear as waiting until their first sign-in, and are in the moment they sign in.${
        lane === "github"
          ? " Tell them yourself: no invite is sent for a GitHub login."
          : " An invite is sent to the address."
      }`}
      heading="h2"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || handle.trim() === ""}>
            {busy ? "Adding…" : "Add"}
          </Button>
        </>
      }
    >
      {err !== undefined && <NotAllowed err={err} what="Add" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          label="Who"
          hint={
            lane === "github"
              ? "Their GitHub login."
              : "Their email address — the one they verify when they sign in here. Nothing else matches."
          }
        >
          <TextInput
            value={handle}
            onChange={setHandle}
            placeholder={lane === "github" ? "alice" : "alice@example.com"}
            mono
            required
            autoFocus
          />
        </Field>
        <div className="field">
          <span className="label">As</span>
          <Choices name="role" value={role} onChange={setRole} options={ROLES} />
        </div>
      </form>
    </FormPage>
  );
}

function AddRule({
  platformName,
  ownName,
  onClose,
  onAdded,
}: {
  platformName: string;
  ownName: string | undefined;
  onClose: () => void;
  onAdded: (row: Row) => void;
}) {
  const s = useSurface();
  const [provider, setProvider] = useState<string>(
    ownName === undefined ? ProviderID.platform : ProviderID.own,
  );
  const [claim, setClaim] = useState("");
  const [value, setValue] = useState("");
  const [role, setRole] = useState<string>(Role.member);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const ready = claim.trim() !== "" && value.trim() !== "";
  const submit = () => {
    if (busy || !ready) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const body: RowPutRequest = {
      row: {
        provider,
        claim: claim.trim(),
        value: value.trim(),
        role: role as Role,
        written: new Date().toISOString(),
      },
    };
    s.bus.call<RowReply>(SERVICE, Subjects.rowsPut(), body).then(
      (reply) => {
        setBusy(false);
        onAdded(reply.row);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  const providers = [
    { value: ProviderID.platform, label: platformName, hint: "What everyone may sign in with." },
    ...(ownName === undefined
      ? []
      : [{ value: ProviderID.own, label: ownName, hint: `${s.name}'s own sign-in.` }]),
  ];
  return (
    <FormPage
      title="Add a rule"
      line="A rule reads one claim of the token a person signs in with and gives everyone it matches a role."
      heading="h2"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !ready}>
            {busy ? "Adding…" : "Add"}
          </Button>
        </>
      }
    >
      {err !== undefined && <NotAllowed err={err} what="Add" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="field">
          <span className="label">Signs in with</span>
          <Choices name="provider" value={provider} onChange={setProvider} options={providers} />
        </div>
        <div className="two">
          <Field label="Claim" hint="roles on Entra, the groups claim on Okta, hd on Google.">
            <TextInput value={claim} onChange={setClaim} placeholder="roles" mono required />
          </Field>
          <Field label="Value">
            <TextInput value={value} onChange={setValue} placeholder="Impire.Admin" mono required />
          </Field>
        </div>
        <div className="field">
          <span className="label">Gives</span>
          <Choices name="rule-role" value={role} onChange={setRole} options={ROLES} />
        </div>
      </form>
    </FormPage>
  );
}

const WARNINGS = {
  self: "This is your own row. You become a member at your next sign-in and can no longer manage people, sign-in or settings.",
  "last-admin":
    "This is the workspace's last admin row. Nothing stops it, but once it is lowered nobody here can manage people or sign-in until the platform operator grants an admin again.",
} as const;

function EditRole({
  row,
  rows,
  who,
  onClose,
  onChanged,
}: {
  row: Row;
  rows: readonly Row[];
  /** The person's name, for a person's row; absent for a rule. */
  who: string | undefined;
  onClose: () => void;
  onChanged: (r: Row) => void;
}) {
  const s = useSurface();
  const [role, setRole] = useState<string>(row.role);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const warning = roleChangeWarning(row, role as Role, rows, s.me);
  const submit = () => {
    if (busy || role === row.role) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const body = roleChange(row, role as Role);
    s.bus.call<RowReply>(SERVICE, Subjects.rowsPut(), body).then(
      (reply) => {
        setBusy(false);
        onChanged(reply.row);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      title={who === undefined ? "Edit the rule" : `Edit ${who}`}
      heading="h2"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || role === row.role}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <p className="muted">
        {row.pending === true
          ? "They have not signed in yet; they get this role at their first sign-in."
          : who === undefined
            ? "Everyone this rule matches gets the new role at their next sign-in."
            : "The new role takes effect at their next sign-in. You can sign them out now afterwards."}
      </p>
      {err !== undefined && <NotAllowed err={err} what="Edit" />}
      {who === undefined && (
        <p className="small">
          <span className="mono">{row.claim}</span> <span className="mono">{row.value}</span>
        </p>
      )}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="field">
          <span className="label">{who === undefined ? "Gives" : "Role"}</span>
          <Choices name="edit-role" value={role} onChange={setRole} options={ROLES} />
        </div>
      </form>
      {warning !== undefined && <Notice kind="bad">{WARNINGS[warning]}</Notice>}
    </FormPage>
  );
}
